/**
 * Generate Service
 * Service for AI content generation and job tracking
 */

import { sharedApiClient } from './client';
import type {
  GenerateContentRequest,
  GenerateContentFromDocumentRequest,
  GenerateContentResponse,
  RegenerateContentRequest,
  BulkContentGenerateRequest,
  BulkContentGenerateResponse,
  BulkGenerateEstimateResponse,
  BulkGeneratedContentsResponse,
  BulkDiscardContentsRequest,
  BulkDiscardContentsResponse,
  BulkJobDetail,
  JobDetail,
  JobStatus,
} from './generate.types';
import { isJobTerminal } from './generate.types';

/**
 * EAA-2063: `?tenant_id=` for the bulk-generate review calls, as the program endpoints
 * send it. A Super Admin has no acting tenant (no X-Tenant-Id), and these views answer
 * 400 BIZ_TENANT_REQUIRED without one.
 */
function tenantQuery(tenantId?: string): string {
  return tenantId ? `?${new URLSearchParams({ tenant_id: tenantId }).toString()}` : '';
}

/** Build a synthetic failed JobDetail when fetch (e.g. timeout) fails */
function createFetchFailedStatus(jobId: string, error: unknown): JobDetail {
  const msg =
    error instanceof Error
      ? error.message
      : typeof (error as { message?: string })?.message === 'string'
        ? (error as { message: string }).message
        : 'Failed to fetch job status';
  return {
    id: jobId,
    tenant: '',
    tenant_slug: '',
    name: '',
    description: '',
    status: 'failed' as JobStatus,
    metadata: {},
    result: null,
    error: msg,
    webhook_url: '',
    webhook_events: [],
    progress: { total_steps: 0, completed_steps: 0, failed_steps: 0, percentage: 0 },
    duration_seconds: 0,
    steps: [],
    created_at: '',
    updated_at: '',
    started_at: null,
    completed_at: null,
  };
}

export interface PollJobStatusOptions {
  /** Polling interval in milliseconds (default: 2000) */
  interval?: number;
  /** Maximum polling attempts before timeout (default: 150 = 5 minutes) */
  maxAttempts?: number;
  /** Max milliseconds the job may stay in 'pending' before we give up (default: unlimited) */
  pendingTimeoutMs?: number;
}

/**
 * Generate Service Class
 * Handles AI content generation and job status tracking
 */
class GenerateService {
  private contentBasePath = '/content';
  private integrationsBasePath = '/integrations';

  /**
   * Start content generation job
   * POST /content/generate
   */
  async generateContent(request: GenerateContentRequest): Promise<GenerateContentResponse> {
    const response = await sharedApiClient.post<GenerateContentResponse>(
      `${this.contentBasePath}/generate`,
      request
    );
    return response.data;
  }

  /**
   * Start content generation from worksheet document + prompt.
   * POST /content/generate-from-document (multipart)
   */
  async generateContentFromDocument(
    request: GenerateContentFromDocumentRequest
  ): Promise<GenerateContentResponse> {
    const formData = new FormData();
    formData.append('file', request.file, request.file.name);
    formData.append('prompt', request.prompt);
    formData.append('question_type', request.question_type);
    if (request.tenant_id) formData.append('tenant_id', request.tenant_id);
    if (request.content_format) formData.append('content_format', request.content_format);
    if (request.title) formData.append('title', request.title);
    if (request.webhook_url) formData.append('webhook_url', request.webhook_url);
    if (request.module_id) formData.append('module_id', request.module_id);
    if (request.status != null) formData.append('status', String(request.status));
    if (request.metadata && Object.keys(request.metadata).length > 0) {
      formData.append('metadata', JSON.stringify(request.metadata));
    }
    if (request.option && Object.keys(request.option).length > 0) {
      formData.append('option', JSON.stringify(request.option));
    }

    const response = await sharedApiClient.postFormData<GenerateContentResponse>(
      `${this.contentBasePath}/generate-from-document`,
      formData,
      { timeoutMs: 120_000 }
    );
    return response.data;
  }

  /**
   * Queue multiple AI content generation jobs
   * POST /content/bulk-generate
   */
  async bulkGenerateContent(
    request: BulkContentGenerateRequest
  ): Promise<BulkContentGenerateResponse> {
    const response = await sharedApiClient.post<BulkContentGenerateResponse>(
      `${this.contentBasePath}/bulk-generate`,
      request,
      { timeoutMs: 600_000 }
    );
    return response.data;
  }

  /**
   * EAA-2027: estimate how long a bulk generation would take, without queuing it.
   * POST /content/bulk-generate/estimate/ — same body as `bulkGenerateContent`.
   */
  async estimateBulkGenerate(
    request: BulkContentGenerateRequest
  ): Promise<BulkGenerateEstimateResponse> {
    const response = await sharedApiClient.post<BulkGenerateEstimateResponse>(
      `${this.contentBasePath}/bulk-generate/estimate/`,
      request
    );
    return response.data;
  }

  /**
   * EAA-2063: what a bulk generation saved, for the "Chọn câu hỏi" review.
   * GET /content/bulk-generate/{parentJobId}/contents/
   */
  async getBulkGeneratedContents(
    parentJobId: string,
    tenantId?: string
  ): Promise<BulkGeneratedContentsResponse> {
    const response = await sharedApiClient.get<BulkGeneratedContentsResponse>(
      `${this.contentBasePath}/bulk-generate/${parentJobId}/contents/${tenantQuery(tenantId)}`
    );
    return response.data;
  }

  /**
   * EAA-2063: delete items of a bulk generation the user did not keep.
   * POST /content/bulk-generate/{parentJobId}/discard/
   */
  async discardBulkGeneratedContents(
    parentJobId: string,
    request: BulkDiscardContentsRequest,
    tenantId?: string
  ): Promise<BulkDiscardContentsResponse> {
    const response = await sharedApiClient.post<BulkDiscardContentsResponse>(
      `${this.contentBasePath}/bulk-generate/${parentJobId}/discard/${tenantQuery(tenantId)}`,
      request
    );
    return response.data;
  }

  /**
   * Regenerate existing content with AI
   * POST /content/{id}/regenerate
   * Updates existing content instead of creating new
   */
  async regenerateContent(
    contentId: string,
    request: RegenerateContentRequest
  ): Promise<GenerateContentResponse> {
    const response = await sharedApiClient.post<GenerateContentResponse>(
      `${this.contentBasePath}/${contentId}/regenerate`,
      request
    );
    return response.data;
  }

  /**
   * Get job status with steps
   * GET /integrations/jobs/{id}
   *
   * When `sessionToken` is provided, uses raw fetch with `Authorization: Session <token>`
   * so student tokens (which lack `job.read`) can still poll essay/speaking grade jobs
   * authenticated via the session JWT instead of the global view-token.
   *
   * For single-player mode (no sessionToken), falls back to sharedApiClient which should
   * be configured with a token that has `job.read` permission (typically an API token).
   * If the configured view-token lacks `job.read`, the backend will return 403 permission_denied.
   */
  async getJobStatus(jobId: string, sessionToken?: string): Promise<JobDetail> {
    if (sessionToken) {
        const { getApiConfig } = await import('./client');
        const config = getApiConfig();
        const url = `${config.baseUrl}${this.integrationsBasePath}/jobs/${jobId}`;
        const res = await fetch(url, {
            headers: { Authorization: `Session ${sessionToken}` },
        });
        const json = (await res.json().catch(() => null)) as { data?: JobDetail } | null;
        if (!res.ok) {
            throw new Error(`getJobStatus failed (${res.status})`);
        }
        return (json?.data ?? json) as JobDetail;
    }
    const response = await sharedApiClient.get<JobDetail>(
      `${this.integrationsBasePath}/jobs/${jobId}`
    );
    return response.data;
  }

  /**
   * Poll parent bulk job with all child statuses
   * GET /content/bulk-generate/{parentJobId}?minimal=true
   */
  async getBulkJobDetail(parentJobId: string, minimal = true): Promise<BulkJobDetail> {
    const qs = minimal ? '?minimal=true' : '';
    const response = await sharedApiClient.get<BulkJobDetail>(
      `${this.contentBasePath}/bulk-generate/${parentJobId}${qs}`
    );
    return response.data;
  }

  /**
   * Poll job status until completed/failed/cancelled
   * Calls onStatusChange callback on each poll
   * Returns final job status
   *
   * When `sessionToken` is provided in options, polls with Session auth instead of
   * the global view-token — required for student essay/speaking grading jobs.
   */
  async pollJobStatus(
    jobId: string,
    onStatusChange: (status: JobDetail) => void,
    options?: PollJobStatusOptions & { sessionToken?: string }
  ): Promise<JobDetail> {
    const interval = options?.interval ?? 2000;
    const maxAttempts = options?.maxAttempts ?? 150;
    const pendingTimeoutMs = options?.pendingTimeoutMs;
    const sessionToken = options?.sessionToken;

    let attempts = 0;
    let lastStatus: JobDetail | null = null;
    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          attempts++;
          const status = await this.getJobStatus(jobId, sessionToken);
          lastStatus = status;
          onStatusChange(status);

          if (isJobTerminal(status.status)) {
            resolve(status);
            return;
          }

          // Reject if job stuck in 'pending' too long (worker never picked it up)
          if (
            pendingTimeoutMs &&
            status.status === 'pending' &&
            Date.now() - startTime > pendingTimeoutMs
          ) {
            reject(
              new Error(
                `Job ${jobId} stuck in pending for ${Math.round((Date.now() - startTime) / 1000)}s`
              )
            );
            return;
          }

          if (attempts >= maxAttempts) {
            reject(new Error(`Polling timeout after ${maxAttempts} attempts`));
            return;
          }

          setTimeout(poll, interval);
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  }

  /**
   * Create a cancellable polling controller
   * Returns an object with poll() and cancel() methods
   */
  createPollingController(
    jobId: string,
    onStatusChange: (status: JobDetail) => void,
    options?: PollJobStatusOptions
  ): { start: () => void; cancel: () => void; isPolling: () => boolean } {
    const interval = options?.interval ?? 2000;
    const maxAttempts = options?.maxAttempts ?? 150;

    let attempts = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    let polling = false;

    const poll = async () => {
      if (cancelled) {
        polling = false;
        return;
      }

      try {
        attempts++;
        const status = await this.getJobStatus(jobId);
        
        if (cancelled) {
          polling = false;
          return;
        }

        onStatusChange(status);

        if (isJobTerminal(status.status)) {
          polling = false;
          return;
        }

        if (attempts >= maxAttempts) {
          polling = false;
          onStatusChange({
            ...status,
            status: 'failed' as JobStatus,
            error: `Polling timeout after ${maxAttempts} attempts`,
          });
          return;
        }

        timeoutId = setTimeout(poll, interval);
      } catch (error) {
        polling = false;
        console.error('Polling error:', error);
        // Notify UI to stop polling and show error (e.g. fetch timeout)
        if (!cancelled) {
          const failedStatus = createFetchFailedStatus(jobId, error);
          onStatusChange(failedStatus);
        }
      }
    };

    return {
      start: () => {
        if (polling) return;
        cancelled = false;
        polling = true;
        attempts = 0;
        poll();
      },
      cancel: () => {
        cancelled = true;
        polling = false;
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      },
      isPolling: () => polling,
    };
  }
}

export const generateService = new GenerateService();

export default generateService;
